import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Popconfirm, message, Typography, Tag, Space, Spin, Rate, Select } from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReviewsByStatus, updateReviewStatus } from '../ListUserReviews/ListUserReviewsAction'; 
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ListUserReviews = ({ refreshTrigger = 0 }) => {
    const dispatch = useDispatch();

    const listUserReviewsState = useSelector(state => state.listUserReviews);
    
    const reviewsData = listUserReviewsState?.reviews?.data;
    const reviews = reviewsData?.reviews || 
                   reviewsData?.content || 
                   (Array.isArray(reviewsData) ? reviewsData : []);
    
    const totalElements = reviewsData?.totalElements || 0;
    
    const loading = listUserReviewsState?.reviews?.loading || false;
    const error = listUserReviewsState?.reviews?.error?.status || false;
    
    const [updatingId, setUpdatingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [statusFilter, setStatusFilter] = useState('PENDING');

    useEffect(() => {
        dispatch(fetchReviewsByStatus(currentPage, pageSize, statusFilter));
    }, [dispatch, refreshTrigger, currentPage, pageSize, statusFilter]);

    const handleUpdate = async (id, status) => {
        setUpdatingId(id);
        try {
            await dispatch(updateReviewStatus(
                id, 
                status,
                () => {
                    dispatch(fetchReviewsByStatus(currentPage, pageSize, statusFilter));
                    setUpdatingId(null);
                },
                (error) => {
                    console.error('Update error:', error);
                    message.error('Failed to update review');
                    setUpdatingId(null);
                }
            ));
        } catch (err) {
            console.error('Catch error:', err);
            message.error('Failed to update review');
            setUpdatingId(null);
        }
    };

    const handleRefresh = () => {
        dispatch(fetchReviewsByStatus(currentPage, pageSize, statusFilter));
    };

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(1); 
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            'PENDING': { color: 'orange', text: 'Pending' },
            'APPROVED': { color: 'green', text: 'Approved' },
            'REJECTED': { color: 'red', text: 'Rejected' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'User & Space',
            key: 'userAndSpace',
            render: (_, record) => (
                <div>
                    <Text strong>{record.user?.firstName || 'Anonymous'} {record.user?.lastName || ''}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        for {record?.spaceName || 'Unknown Space'}
                    </Text>
                </div>
            ),
            width: '25%',
        },
        {
            title: 'Rating',
            key: 'rating',
            render: (_, record) => (
                <Rate disabled defaultValue={record.rating} style={{ fontSize: 16 }} />
            ),
            width: '15%',
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            render: (text) => (
                <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>
                    {text || 'No comment'}
                </Paragraph>
            ),
            width: '25%',
        },
        {
            title: 'Status',
            dataIndex: 'reviewStatus',
            key: 'reviewStatus',
            render: (status) => getStatusTag(status),
            width: '10%',
        },
        {
            title: 'Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            render: (date) => moment(date).format('YYYY-MM-DD'),
            width: '10%',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {record.reviewStatus === 'PENDING' && (
                        <>
                            <Popconfirm
                                title="Approve Review"
                                description="Are you sure you want to approve this review?"
                                onConfirm={() => handleUpdate(record.id, 'APPROVED')}
                                okText="Yes"
                                cancelText="No"
                                disabled={updatingId !== null}
                            >
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    size="small"
                                    loading={updatingId === record.id}
                                    disabled={updatingId !== null && updatingId !== record.id}
                                >
                                    Approve
                                </Button>
                            </Popconfirm>
                            <Popconfirm
                                title="Reject Review"
                                description="Are you sure you want to reject this review?"
                                onConfirm={() => handleUpdate(record.id, 'REJECTED')}
                                okText="Yes"
                                cancelText="No"
                                okType="danger"
                                disabled={updatingId !== null}
                            >
                                <Button
                                    type="primary"
                                    danger
                                    icon={<CloseOutlined />}
                                    size="small"
                                    loading={updatingId === record.id}
                                    disabled={updatingId !== null && updatingId !== record.id}
                                >
                                    Reject
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                    {record.reviewStatus !== 'PENDING' && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.reviewStatus === 'APPROVED' ? 'Already Approved' : 'Already Rejected'}
                        </Text>
                    )}
                </Space>
            ),
            width: '15%',
        },
    ];

    if (error) {
        return (
            <div className="list-user-reviews" style={{ marginTop: '24px' }}>
                <Card title="Reviews Management">
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        color: '#ff4d4f'
                    }}>
                        <Text type="danger">
                            Failed to load reviews. Please try again.
                        </Text>
                        <br />
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            style={{ marginTop: '16px' }}
                        >
                            Retry
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="list-user-reviews" style={{ marginTop: '24px' }}>
            <Card
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>
                            Reviews Management ({totalElements} {statusFilter.toLowerCase()})
                        </Title>
                        <Space>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                style={{ width: 150 }}
                                suffixIcon={<FilterOutlined />}
                            >
                                <Option value="PENDING">Pending</Option>
                                <Option value="APPROVED">Approved</Option>
                                <Option value="REJECTED">Rejected</Option>
                            </Select>
                            <Button
                                type="default"
                                icon={<ReloadOutlined />}
                                onClick={handleRefresh}
                                loading={loading}
                                size="small"
                            >
                                Refresh
                            </Button>
                        </Space>
                    </div>
                }
                className="reviews-list-card"
            >
                {loading ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px'
                    }}>
                        <Spin size="large" tip={`Loading ${statusFilter.toLowerCase()} reviews...`} />
                    </div>
                ) : !Array.isArray(reviews) || reviews.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        color: '#999'
                    }}>
                        <Text>No {statusFilter.toLowerCase()} reviews found.</Text>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={reviews}
                        rowKey="id"
                        loading={updatingId !== null}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalElements,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} reviews`,
                            pageSizeOptions: ['10', '25', '50', '100'],
                        }}
                        onChange={handleTableChange}
                        size="middle"
                    />
                )}
            </Card>
        </div>
    );
};

export default ListUserReviews;